import { useCallback, useEffect, useState } from "react";
import useApi from "./useApi";
import { getWorkflowMyWork } from "../services/workflowService";
import { WorkflowMyWorkResultDto, WorkflowViewerRoleDto } from "../types/workflowDto";
import { toWorkflowMyWorkItem, WorkflowMyWorkItem } from "../features/workflows/workflowInstancesData";

/** The inbox is a short list; the oldest 50 items are what matters. */
const MY_WORK_PAGE_SIZE = 50;

export interface WorkflowMyWorkState {
  items: WorkflowMyWorkItem[];
  total: number;
  /** The role the server built the inbox for - the empty state names it. */
  viewerRole: WorkflowViewerRoleDto | null;
  loading: boolean;
  error: boolean;
}

/**
 * Epic 80431 - the My Work inbox. Fetched on its own, so a failure here never blanks the Instances
 * table (and the reverse). Which items appear - and for which role - is the server's decision.
 * Pass `enabled = false` when the user lacks My Work access, so no request is made at all.
 */
const useWorkflowMyWork = (enabled: boolean) => {
  const { executeApi } = useApi();
  const [state, setState] = useState<WorkflowMyWorkState>({
    items: [],
    total: 0,
    viewerRole: null,
    loading: enabled,
    error: false,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setState({ items: [], total: 0, viewerRole: null, loading: false, error: false });
      return undefined;
    }
    let active = true;
    setState((current) => ({ ...current, loading: true, error: false }));

    executeApi<{ page: number; pageSize: number }, WorkflowMyWorkResultDto>(getWorkflowMyWork, {
      page: 1,
      pageSize: MY_WORK_PAGE_SIZE,
    }).then((result) => {
      if (!active) return;
      if (!result) {
        setState((current) => ({ ...current, items: [], loading: false, error: true }));
        return;
      }
      setState({
        items: result.items.map(toWorkflowMyWorkItem),
        total: result.total,
        viewerRole: result.viewerRole,
        loading: false,
        error: false,
      });
    });

    return () => {
      active = false;
    };
  }, [enabled, executeApi, attempt]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  return { ...state, retry };
};

export default useWorkflowMyWork;
